import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { useDashboardAgents } from '../../../hooks/queries/useDashboardAgents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import useAuth from '../../../hooks/useAuth';
import { Agent, AgentType } from '../../../interfaces/Agent';
import { DropDownOption } from '../../shared/DropDown/DropDown';
import { useTranslation } from '../../../hooks/useTranslation';
import { useDashboardChatAgents } from '../../../hooks/queries/useDashboardChatAgents';
import { ChatAgent } from '../../../interfaces/ChatAgent';
import { ALL_AGENTS } from '../../../constants/dashboardConstants';

interface FilterByAgentDropdownProps {
  initialValue?: string;
  onChange?: Dispatch<SetStateAction<string>>;
  agentType: AgentType;
}

const remapDropDownAgentsList = (agentsList: Agent[]): DropDownOption[] => {
  return agentsList
    .filter((agent: Agent) => agent.id) // Filter out agents without IDs
    .map((agent: Agent) => ({
      label: agent.name,
      value: agent.id!, // Use non-null assertion since we filtered
    }));
};

const remapDropDownChatAgentsList = (
  agentsList: ChatAgent[]
): DropDownOption[] => {
  return agentsList
    .filter((agent: ChatAgent) => agent.id) // Filter out agents without IDs
    .map((agent: ChatAgent) => ({
      label: agent.name ?? '',
      value: agent.id!, // Use non-null assertion since we filtered
    }));
};

const FilterByAgentDropdown: React.FC<FilterByAgentDropdownProps> = ({
  initialValue = ALL_AGENTS,
  onChange,
  agentType,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { track } = useAnalytics();
  const [value, setValue] = useState<string>(initialValue);

  // Fetch agents list based on type - hooks must be called at top level
  const { data: callAgentsList } = useDashboardAgents(
    agentType === AgentType.CALL && !!currentUser
  );
  const { data: chatAgentsList } = useDashboardChatAgents(
    agentType === AgentType.CHAT && !!currentUser
  );

  // Transform agents list to dropdown options
  const options = useMemo(() => {
    if (agentType === AgentType.CHAT) {
      return remapDropDownChatAgentsList(chatAgentsList ?? []);
    }
    return remapDropDownAgentsList(callAgentsList ?? []);
  }, [agentType, callAgentsList, chatAgentsList]);

  // Track agent selection changes
  useEffect(() => {
    if (value !== ALL_AGENTS) {
      track(AnalyticsEvents.AGENT_SELECTION_CHANGED, {
        agentId: value,
      });
    }
  }, [value, track]);

  // Reset value when type changes
  useEffect(() => {
    setValue(ALL_AGENTS);
  }, [agentType]);

  // Notify parent of value changes
  useEffect(() => {
    onChange && onChange(value);
  }, [value, onChange]);

  return (
    <Select
      value={value}
      onChange={(event: SelectChangeEvent<string>) =>
        setValue(event.target.value)
      }
      sx={{
        width: '250px',
        minHeight: 42,
        borderRadius: '10px',
        bgcolor: '#FFFFFF',
      }}
    >
      <MenuItem key="filter-by-agent" value="--">
        {t('All agents')}
      </MenuItem>
      {options.map((option: DropDownOption) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export default FilterByAgentDropdown;
